import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Settings, ArrowLeft, Save, Loader2, Trash2 } from 'lucide-react';
import { useProjects } from '../hooks/useProjects';
import { DeleteProjectDialog } from '../components/projects';
import { ProjectDefaultsSettings } from '../components/permissions';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ProjectSettingsFormProps {
  project: {
    id: string;
    name: string;
    slug: string;
    description?: string;
  };
  updateProject: (args: {
    projectId: string;
    data: { name?: string; description?: string };
  }) => void;
  isUpdatingProject: boolean;
  updateProjectError: Error | null;
  deleteProject: (projectId: string, options?: { onSuccess?: () => void }) => void;
  isDeletingProject: boolean;
  deleteProjectError: Error | null;
  onDeleteSuccess: () => void;
}

/**
 * Inner form component - gets remounted when project changes via key
 */
function ProjectSettingsForm({
  project,
  updateProject,
  isUpdatingProject,
  updateProjectError,
  deleteProject,
  isDeletingProject,
  deleteProjectError,
  onDeleteSuccess,
}: ProjectSettingsFormProps) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Calculate hasChanges derived from state
  const hasChanges = useMemo(() => {
    const nameChanged = name !== project.name;
    const descChanged = description !== (project.description || '');
    return nameChanged || descChanged;
  }, [name, description, project]);

  const handleSave = () => {
    if (!hasChanges || isUpdatingProject) return;

    updateProject({
      projectId: project.id,
      data: {
        name: name.trim(),
        description: description.trim() || undefined,
      },
    });
  };

  const handleDelete = () => {
    if (isDeletingProject) return;

    deleteProject(project.id, {
      onSuccess: () => {
        setIsDeleteDialogOpen(false);
        onDeleteSuccess();
      },
    });
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </Link>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-muted rounded-lg">
            <Settings className="h-6 w-6 text-muted-foreground" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Project Settings</h1>
            <p className="text-muted-foreground">{project.name}</p>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>General</CardTitle>
          <CardDescription>
            Basic project information and settings
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Error message */}
          {updateProjectError && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
              {updateProjectError.message ||
                'Failed to update project. Please try again.'}
            </div>
          )}

          {/* Project name */}
          <div className="space-y-2">
            <Label htmlFor="project-name">Project Name</Label>
            <Input
              type="text"
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="max-w-md"
              disabled={isUpdatingProject}
            />
          </div>

          {/* Project description */}
          <div className="space-y-2">
            <Label htmlFor="project-description">
              Description <span className="text-muted-foreground">(optional)</span>
            </Label>
            <textarea
              id="project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="flex min-h-[80px] w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              disabled={isUpdatingProject}
            />
          </div>

          {/* Project slug (read-only) */}
          <div className="space-y-2">
            <Label>Project Slug</Label>
            <div className="w-full max-w-md px-3 py-2 bg-muted border border-border rounded-lg text-muted-foreground font-mono text-sm">
              {project.slug}
            </div>
            <p className="text-xs text-muted-foreground">
              The slug is auto-generated and cannot be changed
            </p>
          </div>

          {/* Save button */}
          <div className="pt-4">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || isUpdatingProject || !name.trim()}
            >
              {isUpdatingProject ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Default Permissions */}
      <div className="mt-8">
        <ProjectDefaultsSettings projectId={project.id} />
      </div>

      {/* Danger Zone */}
      <Card className="mt-8 border-destructive/50 overflow-hidden">
        <CardHeader className="bg-destructive/5 border-b border-destructive/20">
          <CardTitle className="text-destructive">Danger Zone</CardTitle>
          <CardDescription className="text-destructive/80">
            Irreversible and destructive actions
          </CardDescription>
        </CardHeader>

        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-sm font-medium text-foreground">
                Delete this project
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Once you delete a project, there is no going back. This will
                permanently delete the project and all of its environments,
                secrets, and configurations.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(true)}
              className="ml-4 border-destructive text-destructive hover:bg-destructive/10 flex-shrink-0"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Project
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <DeleteProjectDialog
        isOpen={isDeleteDialogOpen}
        projectName={project.name}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        isLoading={isDeletingProject}
        error={deleteProjectError}
      />
    </div>
  );
}

/**
 * Project settings page - rename and delete project
 * Uses key-based remounting to reset form state when project changes
 */
export function ProjectSettingsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const {
    projects,
    isLoadingProjects,
    updateProject,
    isUpdatingProject,
    updateProjectError,
    deleteProject,
    isDeletingProject,
    deleteProjectError,
  } = useProjects();

  const project = projects.find((p) => p.id === projectId);

  // Loading state
  if (isLoadingProjects) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 text-primary animate-spin" />
        </div>
      </div>
    );
  }

  // Project not found
  if (!project) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Project not found
            </h2>
            <p className="text-muted-foreground mb-4">
              The project you're looking for doesn't exist or has been deleted.
            </p>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 text-primary hover:text-primary/80"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Projects
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <ProjectSettingsForm
      key={project.id}
      project={project}
      updateProject={updateProject}
      isUpdatingProject={isUpdatingProject}
      updateProjectError={updateProjectError}
      deleteProject={deleteProject}
      isDeletingProject={isDeletingProject}
      deleteProjectError={deleteProjectError}
      onDeleteSuccess={() => navigate('/projects')}
    />
  );
}
